import React from 'react';
import { DocumentArrowDownIcon, ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useSuccessionTheme } from './SuccessionThemeProvider';
import { S3Image } from '../../lib/s3';
import { pctSymbol } from '../../utils/formatters';

const BcpTile = ({ bcpData }) => {
  // Use succession theme context
  const { theme, isLightTheme, isDarkTheme, company } = useSuccessionTheme();
  
  // Provide defaults if bcpData is undefined
  const safeData = bcpData || {};
  
  // Status can be 'not_started', 'in_progress', or 'completed'
  const status = safeData?.status || 'not_started';
  const completionPercentage = safeData?.completionPercentage || 0;
  const currentSection = safeData?.currentSection || '';
  
  // Status-based Tailwind classes
  const getStatusClasses = () => {
    switch (status) {
      case 'completed':
        return { 
          color: 'text-succession-success', 
          bgColor: 'bg-succession-success-10',
          progressColor: 'bg-succession-success'
        };
      case 'in_progress':
        return { 
          color: 'text-succession-warning', 
          bgColor: 'bg-succession-warning-10',
          progressColor: 'bg-succession-warning'
        };
      default:
        return { 
          color: 'text-succession-text-secondary', 
          bgColor: 'bg-succession-bg-tertiary',
          progressColor: 'bg-succession-text-secondary'
        };
    }
  };
  
  const statusClasses = getStatusClasses();
  const showWarningShield = status === 'not_started' || completionPercentage === 0;
  
  return (
    <div className="bg-succession-bg-primary shadow-succession-md rounded-lg p-6 border border-succession-border transition-colors duration-300">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-succession-text-primary transition-colors duration-300">
          Business Continuity Plan
        </h3>
        <ExclamationTriangleIcon className="h-6 w-6 text-succession-warning transition-colors duration-300" />
      </div>
      
      <div className="mt-4 mb-5">
        <div className="relative pt-1">
          {showWarningShield ? (
            <div>
              <span
                className={`text-xs font-semibold inline-block py-2 px-3 rounded-md text-succession-error bg-succession-error-bg transition-colors duration-300`}
              >
                Your business is at risk of a significant business disruption
              </span>
            </div>
          ) : (
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span 
                  className={`text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full ${statusClasses.color} ${statusClasses.bgColor} transition-colors duration-300`}
                >
                  {status === 'in_progress' && currentSection ? `Section: ${currentSection}` : 
                   status === 'completed' ? 'Continuity Plan Complete' : 
                   'Create your Business Continuity Plan'}
                </span>
              </div>
              <div className="text-right">
                <span
                  className={`text-xs font-semibold inline-block ${statusClasses.color} transition-colors duration-300`}
                >
                  {completionPercentage}{pctSymbol()}
                </span>
              </div>
            </div>
          )}
          {showWarningShield ? (
            <div className="flex items-center justify-center h-28 mb-2">
              <img
                src={S3Image('no_continuity_plan.png')}
                alt="Warning"
                className="w-auto object-contain"
                style={{ height: '7.5rem' }}
              />
            </div>
          ) : (
            <div 
              className={`overflow-hidden h-2 mb-4 text-xs flex rounded ${statusClasses.bgColor} transition-colors duration-300`}
            >
              <div
                style={{ width: `${completionPercentage}%` }}
                className={`shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center ${statusClasses.progressColor} transition-colors duration-300`}
              ></div>
            </div>
          )}
        </div>
      </div>
      
      <div className="mt-6">
        {status === 'not_started' && (
          <a
            href="/succession/bcp"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-primary hover:opacity-90 transition-all duration-300"
          >
            Start BCP
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </a>
        )}
        
        {status === 'in_progress' && (
          <a
            href="/succession/bcp"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-warning hover:opacity-90 transition-all duration-300"
          >
            Continue BCP
            <ArrowRightIcon className="ml-2 h-4 w-4" />
          </a>
        )}
        
        {status === 'completed' && (
          <div className="space-y-2">
            <a
              href="/succession/bcp"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-succession-success hover:opacity-90 transition-all duration-300"
            >
              Edit BCP
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default BcpTile; 